import { useState, useEffect, useCallback, useRef } from 'react';
import {
  HiCube,
  HiPlus,
  HiPencil,
  HiTrash,
  HiRefresh,
  HiExclamationCircle,
  HiSearch,
  HiCurrencyDollar,
  HiTag,
  HiExclamation,
  HiQrcode,
  HiPhotograph,
  HiX,
  HiViewGrid,
  HiViewList,
} from 'react-icons/hi';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import ProductThumb from '../components/products/ProductThumb';
import { isAdmin } from '../utils/auth';
import { getImageSrc } from '../utils/media';
import { useAlert } from '../contexts/AlertContext';

function getApiBase() {
  return `/api/${isAdmin() ? 'admin' : 'user'}`;
}

function getHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// No Content-Type: the browser has to set it itself so the multipart boundary
// is included.
function getUploadHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Mirrors MAX_IMAGES_PER_PRODUCT on the API, which rejects anything above it.
const MAX_IMAGES = 6;

// The cards are the default view; the table is still there for the columns the
// cards leave out (purchase price, minimum, last update).
const VIEW_STORAGE_KEY = 'productsView';

export default function Products() {
  const showAlert = useAlert();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', barcode: '', categoryId: '', purchasePrice: '', salePrice: '', quantity: '', minQuantity: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [categories, setCategories] = useState([]);
  // Images already stored on the product being edited, the files picked in this
  // session, and the ids the API is being asked to drop on save.
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_STORAGE_KEY) || 'grid');
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  const imageCount = existingImages.length + newImages.length;

  const handleImagesPick = (event) => {
    const picked = Array.from(event.target.files || []);
    // Reset the input so picking the same file again still fires onChange.
    event.target.value = '';
    if (!picked.length) return;

    const room = MAX_IMAGES - imageCount;
    if (room <= 0) {
      showAlert({ type: 'warning', title: 'عدد الصور', message: `الحد الأقصى ${MAX_IMAGES} صور للمنتج` });
      return;
    }
    if (picked.length > room) {
      showAlert({ type: 'warning', title: 'عدد الصور', message: `تمت إضافة ${room} صورة فقط، الحد الأقصى ${MAX_IMAGES}` });
    }

    setNewImages((prev) => [
      ...prev,
      ...picked.slice(0, room).map((file) => ({ file, preview: URL.createObjectURL(file) })),
    ]);
  };

  const handleRemoveExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((image) => image.id !== id));
    setRemovedImageIds((prev) => [...prev, id]);
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const checkBarcodeDuplicate = (barcode) => {
    const trimmed = barcode.trim();
    if (!trimmed) return;
    const duplicate = products.find(
      (p) => p.barcode === trimmed && (!editing || p.id !== editing.id)
    );
    if (duplicate) {
      showAlert({ type: 'warning', title: 'باركود مكرر', message: `الباركود "${trimmed}" موجود بالفعل للمنتج "${duplicate.name}"` });
    }
  };

  useEffect(() => {
    if (!showForm) return;
    let buffer = '';
    let lastKeyTime = 0;
    let resetTimer = null;

    const handleKeyDown = (e) => {
      if (document.activeElement === barcodeInputRef.current) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const now = Date.now();
      const gap = now - lastKeyTime;
      lastKeyTime = now;

      if (gap > 50) buffer = '';

      if (e.key === 'Enter') {
        clearTimeout(resetTimer);
        if (buffer.length >= 3) {
          e.preventDefault();
          e.stopPropagation();
          setForm((prev) => ({ ...prev, barcode: buffer }));
          checkBarcodeDuplicate(buffer);
          barcodeInputRef.current?.focus();
        }
        buffer = '';
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;
        clearTimeout(resetTimer);
        resetTimer = setTimeout(() => { buffer = ''; }, 100);

        if (buffer.length >= 2 && gap <= 50) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      clearTimeout(resetTimer);
    };
  }, [showForm, products, editing]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${getApiBase()}/products`, { headers: getHeaders() });
      if (!res.ok) throw new Error(`فشل في تحميل المنتجات (${res.status})`);
      const data = await res.json();
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setProducts(data);
    } catch (err) {
      showAlert({ type: 'error', title: 'خطأ', message: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBase()}/categories`, { headers: getHeaders() });
      if (res.ok) setCategories(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.salePrice) return;
    if (form.barcode.trim()) {
      const duplicate = products.find(
        (p) => p.barcode === form.barcode.trim() && (!editing || p.id !== editing.id)
      );
      if (duplicate) {
        showAlert({ type: 'warning', title: 'باركود مكرر', message: `الباركود "${form.barcode.trim()}" موجود بالفعل للمنتج "${duplicate.name}"` });
        return;
      }
    }
    setSaving(true);
    try {
      const base = getApiBase();
      const url = editing ? `${base}/products/${editing.id}` : `${base}/products`;
      const method = editing ? 'PUT' : 'POST';

      // FormData throughout, so one code path covers a plain edit and an edit
      // that also changes the images.
      const payload = new FormData();
      payload.append('name', form.name);
      payload.append('barcode', form.barcode);
      if (form.categoryId) payload.append('categoryId', form.categoryId);
      payload.append('purchasePrice', parseFloat(form.purchasePrice) || 0);
      payload.append('salePrice', parseFloat(form.salePrice) || 0);
      payload.append('quantity', parseInt(form.quantity) || 0);
      payload.append('minQuantity', parseInt(form.minQuantity) || 0);

      newImages.forEach(({ file }) => payload.append('images', file));
      if (editing) {
        removedImageIds.forEach((id) => payload.append('removeImageIds', id));
        // The kept images in the order shown; the new uploads land after them.
        existingImages.forEach((image) => payload.append('imageOrder', image.id));
      }

      const res = await fetch(url, { method, headers: getUploadHeaders(), body: payload });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const msg = data?.message || 'فشل في حفظ المنتج';
        if (msg.toLowerCase().includes('barcode already exists')) {
          showAlert({ type: 'warning', title: 'باركود مكرر', message: 'هذا الباركود موجود بالفعل لمنتج آخر' });
          setSaving(false);
          return;
        }
        throw new Error(msg);
      }

      closeForm();
      fetchProducts();
    } catch (err) {
      showAlert({ type: 'error', title: 'خطأ', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.id);
    try {
      const res = await fetch(`${getApiBase()}/products/${deleteTarget.id}`, { method: 'DELETE', headers: getHeaders() });
      if (!res.ok) throw new Error('فشل في حذف المنتج');
      setDeleteTarget(null);
      fetchProducts();
    } catch (err) {
      showAlert({ type: 'error', title: 'خطأ', message: err.message });
    } finally {
      setDeleting(null);
    }
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({
      name: product.name,
      barcode: product.barcode || '',
      categoryId: product.categoryId || '',
      purchasePrice: product.purchasePrice || '',
      salePrice: product.salePrice || '',
      quantity: product.quantity?.toString() || '',
      minQuantity: product.minQuantity?.toString() || '',
    });
    setExistingImages(product.images || []);
    setNewImages([]);
    setRemovedImageIds([]);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ name: '', barcode: '', categoryId: '', purchasePrice: '', salePrice: '', quantity: '', minQuantity: '' });
    newImages.forEach(({ preview }) => URL.revokeObjectURL(preview));
    setExistingImages([]);
    setNewImages([]);
    setRemovedImageIds([]);
  };

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode || '').includes(search) ||
    (p.category?.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalValue = products.reduce((sum, p) => sum + (parseFloat(p.salePrice) || 0) * (p.quantity || 0), 0);
  const lowStock = products.filter((p) => p.quantity <= p.minQuantity).length;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-5 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 animate-fadeInUp" style={{ animationDelay: '0ms' }}>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm text-gray-500 font-medium">إجمالي المنتجات</p>
              <p className="text-3xl font-bold text-gray-800">{products.length}</p>
              <p className="text-xs text-gray-400">منتج مسجل</p>
            </div>
            <div className="p-3 rounded-xl bg-primary-50 text-primary-600 shadow-lg shadow-primary-500/10">
              <HiCube className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-5 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 animate-fadeInUp" style={{ animationDelay: '80ms' }}>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm text-gray-500 font-medium">قيمة المخزون</p>
              <p className="text-3xl font-bold text-gray-800">{totalValue.toFixed(2)} د.ا</p>
              <p className="text-xs text-gray-400">بسعر البيع</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-50 text-blue-600 shadow-lg shadow-blue-500/10">
              <HiCurrencyDollar className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-5 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 animate-fadeInUp" style={{ animationDelay: '160ms' }}>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm text-gray-500 font-medium">التصنيفات</p>
              <p className="text-3xl font-bold text-gray-800">{new Set(products.map(p => p.category?.name)).size}</p>
              <p className="text-xs text-gray-400">تصنيف نشط</p>
            </div>
            <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600 shadow-lg shadow-emerald-500/10">
              <HiTag className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-5 hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 animate-fadeInUp" style={{ animationDelay: '240ms' }}>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm text-gray-500 font-medium">مخزون منخفض</p>
              <p className={`text-3xl font-bold ${lowStock > 0 ? 'text-red-600' : 'text-gray-800'}`}>{lowStock}</p>
              <p className="text-xs text-gray-400">منتج يحتاج إعادة تعبئة</p>
            </div>
            <div className={`p-3 rounded-xl ${lowStock > 0 ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'} shadow-lg`}>
              <HiExclamation className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Search & Actions */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-80">
          <HiSearch className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="ابحث بالاسم أو الباركود أو التصنيف..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-1 p-1 rounded-xl bg-white border border-primary-200/50">
            <button
              onClick={() => setView('grid')}
              title="عرض شبكي"
              className={`p-2 rounded-lg transition-colors ${
                view === 'grid' ? 'bg-primary-500 text-white shadow-sm' : 'text-gray-400 hover:text-primary-500'
              }`}
            >
              <HiViewGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView('table')}
              title="عرض جدول"
              className={`p-2 rounded-lg transition-colors ${
                view === 'table' ? 'bg-primary-500 text-white shadow-sm' : 'text-gray-400 hover:text-primary-500'
              }`}
            >
              <HiViewList className="w-4 h-4" />
            </button>
          </div>
          <Button variant="outline" icon={HiRefresh} onClick={fetchProducts} size="md">
            تحديث
          </Button>
          <Button icon={HiPlus} onClick={() => { closeForm(); setShowForm(true); }}>
            إضافة منتج
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 animate-fadeIn">
          <HiExclamationCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm font-semibold text-red-700 flex-1">{error}</p>
          <button onClick={() => setError('')} className="text-xs text-red-500 hover:text-red-700 font-semibold">إغلاق</button>
        </div>
      )}

      {/* Grid: image on top, name below */}
      {view === 'grid' && (
        loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-primary-100/40 overflow-hidden animate-pulse">
                <div className="aspect-square bg-gray-200" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded" />
                  <div className="h-4 w-1/2 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
            {filtered.map((product, idx) => {
              const isLowStock = product.quantity <= product.minQuantity;
              return (
                <div
                  key={product.id}
                  className="group bg-white rounded-2xl shadow-sm border border-primary-100/40 overflow-hidden hover:shadow-lg hover:-translate-y-1 hover:border-primary-200 transition-all duration-300 animate-fadeInUp"
                  style={{ animationDelay: `${(idx % 10) * 40}ms` }}
                >
                  <div className="relative">
                    <ProductThumb product={product} />
                    {product.category?.name && (
                      <span className="absolute top-2 right-2 px-2 py-0.5 rounded-lg bg-white/90 backdrop-blur-sm text-[10px] font-semibold text-primary-600 shadow-sm">
                        {product.category.name}
                      </span>
                    )}
                    {/* The actions stay out of the way until the card is hovered */}
                    <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEdit(product)}
                        title="تعديل"
                        className="p-1.5 rounded-lg bg-white/90 backdrop-blur-sm text-gray-500 hover:text-primary-600 shadow-sm transition-colors"
                      >
                        <HiPencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(product)}
                        disabled={deleting === product.id}
                        title="حذف"
                        className="p-1.5 rounded-lg bg-white/90 backdrop-blur-sm text-gray-500 hover:text-red-500 shadow-sm transition-colors disabled:opacity-50"
                      >
                        {deleting === product.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                        ) : (
                          <HiTrash className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="p-3">
                    <h3 className="text-sm font-bold text-gray-800 leading-snug line-clamp-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-base font-bold text-primary-600">
                        {parseFloat(product.salePrice).toFixed(2)} د.ا
                      </span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 ${
                        isLowStock ? 'bg-red-50 text-red-500' : 'bg-gray-50 text-gray-400'
                      }`}>
                        {isLowStock && <HiExclamation className="w-3 h-3" />}
                        {product.quantity} متاح
                      </span>
                    </div>
                    {product.barcode && (
                      <p className="text-[10px] text-gray-400 font-mono mt-1.5 truncate">{product.barcode}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 py-16 animate-fadeIn">
            <div className="flex flex-col items-center justify-center text-gray-300">
              <HiCube className="w-12 h-12 mb-3" />
              <p className="text-base font-semibold text-gray-400">لا توجد منتجات</p>
              <p className="text-sm text-gray-300 mt-1">
                {search ? 'جرب تعديل البحث' : 'أضف منتج جديد للبدء'}
              </p>
              {!search && (
                <Button icon={HiPlus} className="mt-4" onClick={() => { closeForm(); setShowForm(true); }}>
                  إضافة منتج
                </Button>
              )}
            </div>
          </div>
        )
      )}

      {/* Table */}
      {view === 'table' && (
      <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 overflow-hidden animate-fadeIn">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-3.5 w-12">#</th>
                <th className="px-4 py-3.5">المنتج</th>
                <th className="px-4 py-3.5 hidden sm:table-cell">الباركود</th>
                <th className="px-4 py-3.5 hidden md:table-cell">التصنيف</th>
                <th className="px-4 py-3.5">سعر الشراء</th>
                <th className="px-4 py-3.5">سعر البيع</th>
                <th className="px-4 py-3.5 hidden lg:table-cell">الكمية</th>
                <th className="px-4 py-3.5 hidden xl:table-cell">الحد الأدنى</th>
                <th className="px-4 py-3.5 hidden xl:table-cell">آخر تحديث</th>
                <th className="px-4 py-3.5 text-left">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(10)].map((_, j) => (
                      <td key={j} className="px-4 py-4"><div className="h-5 w-full max-w-[80px] bg-gray-200 rounded-lg animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length > 0 ? (
                filtered.map((product, idx) => {
                  const isLowStock = product.quantity <= product.minQuantity;
                  return (
                    <tr key={product.id} className={`hover:bg-primary-50/30 transition-colors ${isLowStock ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-4">
                        <span className="text-sm font-bold text-gray-400">{idx + 1}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {product.images?.length ? (
                            <img
                              src={getImageSrc(product.images[0].mediaLink)}
                              alt={product.name}
                              className="w-9 h-9 rounded-lg object-cover shadow-sm border border-gray-100"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-400 flex items-center justify-center shadow-sm">
                              <span className="text-xs font-bold text-white">{product.name.charAt(0)}</span>
                            </div>
                          )}
                          <span className="text-sm font-bold text-gray-800">{product.name}</span>
                          {product.images?.length > 1 && (
                            <span className="text-[10px] font-semibold text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-md">
                              {product.images.length} صور
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <span className="text-sm text-gray-600 font-mono bg-gray-50 px-2 py-0.5 rounded">{product.barcode || '—'}</span>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <Badge color="purple">{product.category?.name || '—'}</Badge>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm font-semibold text-gray-600">{parseFloat(product.purchasePrice).toFixed(2)} د.ا</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm font-bold text-primary-600">{parseFloat(product.salePrice).toFixed(2)} د.ا</span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${isLowStock ? 'text-red-600' : 'text-gray-800'}`}>
                            {product.quantity}
                          </span>
                          {isLowStock && (
                            <span className="flex items-center gap-0.5 text-xs text-red-500 bg-red-50 px-1.5 py-0.5 rounded-md border border-red-200">
                              <HiExclamation className="w-3 h-3" />
                              منخفض
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden xl:table-cell">
                        <span className="text-sm text-gray-500">{product.minQuantity}</span>
                      </td>
                      <td className="px-4 py-4 hidden xl:table-cell">
                        <span className="text-xs text-gray-400">
                          {new Date(product.updatedAt).toLocaleDateString('ar-EG', {
                            month: 'short', day: 'numeric',
                          })}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(product)}
                            className="p-2 rounded-lg hover:bg-primary-50 text-gray-400 hover:text-primary-600 transition-colors"
                            title="تعديل"
                          >
                            <HiPencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(product)}
                            disabled={deleting === product.id}
                            className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            title="حذف"
                          >
                            {deleting === product.id ? (
                              <div className="w-4 h-4 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                            ) : (
                              <HiTrash className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="px-5 py-16">
                    <div className="flex flex-col items-center justify-center text-gray-300">
                      <HiCube className="w-12 h-12 mb-3" />
                      <p className="text-base font-semibold text-gray-400">لا توجد منتجات</p>
                      <p className="text-sm text-gray-300 mt-1">
                        {search ? 'جرب تعديل البحث' : 'أضف منتج جديد للبدء'}
                      </p>
                      {!search && (
                        <Button icon={HiPlus} className="mt-4" onClick={() => { closeForm(); setShowForm(true); }}>
                          إضافة منتج
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Add / Edit Modal */}
      <Modal isOpen={showForm} onClose={closeForm} title={editing ? 'تعديل المنتج' : 'إضافة منتج جديد'} size="lg">
        <div className="space-y-4">
          <div className="flex justify-center mb-2">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-400 flex items-center justify-center shadow-xl shadow-primary-500/20">
              <HiCube className="w-7 h-7 text-white" />
            </div>
          </div>

          {/* Images: the first one is the cover shown in lists and in the app */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">صور المنتج</label>
              <span className="text-xs text-gray-400">{imageCount}/{MAX_IMAGES}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {existingImages.map((image, idx) => (
                <div key={image.id} className="relative group">
                  <img
                    src={getImageSrc(image.mediaLink)}
                    alt={`${form.name} ${idx + 1}`}
                    className="w-20 h-20 rounded-xl object-cover border border-gray-100 shadow-sm"
                  />
                  {idx === 0 && (
                    <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] text-center py-0.5 rounded-b-xl">
                      الصورة الرئيسية
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingImage(image.id)}
                    title="إزالة الصورة"
                    className="absolute -top-1.5 -left-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
                  >
                    <HiX className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {newImages.map((image, idx) => (
                <div key={image.preview} className="relative group">
                  <img
                    src={image.preview}
                    alt={`صورة جديدة ${idx + 1}`}
                    className="w-20 h-20 rounded-xl object-cover border border-gray-100 shadow-sm"
                  />
                  {existingImages.length === 0 && idx === 0 && (
                    <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] text-center py-0.5 rounded-b-xl">
                      الصورة الرئيسية
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveNewImage(idx)}
                    title="إزالة الصورة"
                    className="absolute -top-1.5 -left-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
                  >
                    <HiX className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {imageCount < MAX_IMAGES && (
                <label
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 text-gray-400 cursor-pointer hover:border-primary-400 hover:text-primary-500 transition-colors"
                  title="أضف صوراً للمنتج"
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImagesPick}
                    className="sr-only"
                  />
                  <HiPhotograph className="w-6 h-6" />
                  <span className="text-[10px] font-semibold">إضافة</span>
                </label>
              )}
            </div>

            <p className="text-xs text-gray-400">
              {imageCount > 0 ? 'الصورة الأولى هي الصورة الرئيسية للمنتج' : 'يمكنك إضافة صور للمنتج (اختياري)'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="اسم المنتج"
              placeholder="مثال: كوكا كولا"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">الباركود</label>
              <div className="relative">
                <HiQrcode className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="امسح الباركود أو أدخله يدوياً"
                  ref={barcodeInputRef}
                  value={form.barcode}
                  onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                  onBlur={(e) => checkBarcodeDuplicate(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      checkBarcodeDuplicate(e.target.value);
                      e.target.blur();
                    }
                  }}
                  autoComplete="off"
                  className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/50 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">التصنيف</label>
            <select
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            >
              <option value="">اختر التصنيف</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Input
              label="سعر الشراء"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={form.purchasePrice}
              onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })}
            />
            <Input
              label="سعر البيع"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={form.salePrice}
              onChange={(e) => setForm({ ...form, salePrice: e.target.value })}
            />
            <Input
              label="الكمية"
              type="number"
              placeholder="0"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            />
            <Input
              label="الحد الأدنى"
              type="number"
              placeholder="0"
              value={form.minQuantity}
              onChange={(e) => setForm({ ...form, minQuantity: e.target.value })}
            />
          </div>

          <div className="flex gap-3 pt-3">
            <Button variant="ghost" className="flex-1" onClick={closeForm}>
              إلغاء
            </Button>
            <Button className="flex-1" onClick={handleSubmit} disabled={saving || !form.name.trim()}>
              {saving ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري الحفظ...
                </div>
              ) : (
                editing ? 'حفظ التعديلات' : 'إضافة المنتج'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-scaleIn">
            <div className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <HiExclamationCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">تأكيد الحذف</h3>
              <p className="text-sm text-gray-500 mb-1">هل أنت متأكد من حذف المنتج</p>
              <p className="text-base font-bold text-primary-600 mb-5">"{deleteTarget.name}"؟</p>
              <p className="text-xs text-gray-400 mb-6">لا يمكن التراجع عن هذا الإجراء بعد التأكيد</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold transition-all active:scale-95"
                >
                  إلغاء
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting === deleteTarget.id}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold shadow-lg shadow-red-500/25 transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleting === deleteTarget.id ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      جاري الحذف...
                    </>
                  ) : (
                    'نعم، احذف'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
